import { useState, useEffect } from 'react';
import { useTasks, useArtists } from '../hooks/useFirestore';
import { useAuth } from '../lib/AuthContext';
import { Plus, X, GripVertical, Edit2, Check, AlertTriangle, LayoutList, Columns, EyeOff } from 'lucide-react';

const PRIORITY_GROUPS = [
  { id: 'P0', label: 'P0', color: '#c0392b', max: 3 },
  { id: 'P1', label: 'P1', color: '#e8621a', max: 5 },
  { id: 'P2', label: 'P2', color: '#a87ee8', max: null },
];


const STATUS_COLS = [
  { id: 'todo',       label: 'To Do',       color: 'var(--orange)' },
  { id: 'inprogress', label: 'In Progress', color: 'var(--purple2)' },
  { id: 'done',       label: 'Done',        color: 'var(--teal2)' },
];

const STATUS_META = {
  todo:       { label: 'To Do',       color: 'var(--orange)' },
  inprogress: { label: 'In Progress', color: 'var(--purple2)' },
  done:       { label: 'Done',        color: 'var(--teal2)' },
};

export function Modal({ children, onClose, title }) {
  return (
    <div style={{ position:'fixed', inset:0, zIndex:999, background:'rgba(0,0,0,.7)', backdropFilter:'blur(6px)', display:'flex', alignItems:'center', justifyContent:'center', padding:24 }} onClick={onClose}>
      <div style={{ background:'var(--deep)', border:'1px solid var(--border)', borderRadius:14, padding:28, width:'100%', maxWidth:520, maxHeight:'90vh', overflowY:'auto' }} onClick={e => e.stopPropagation()} className="fade-up">
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
          <h2 style={{ fontSize:17, fontWeight:800 }}>{title}</h2>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--muted)' }}><X size={18}/></button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function Tasks({ activeArtistId }) {
  const { tasks, loading, addTask, updateTask, deleteTask } = useTasks();
  const { artists } = useArtists();
  const { profile, isManager } = useAuth();
  const [showAdd, setShowAdd] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [filterArtist, setFilterArtist] = useState(activeArtistId || 'all');
  // Sync when sidebar switcher changes
  useEffect(() => { setFilterArtist(activeArtistId || 'all'); }, [activeArtistId]);
  const [demoteModal, setDemoteModal] = useState(null);
  const [dragTask, setDragTask] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'columns'
  const [hideDone, setHideDone] = useState(false);
  const [form, setForm] = useState({ title:'', artistId:'', priority:'P1', category:'music', status:'todo', dueDate:'', notes:'', personal:false });

  const visibleTasks = tasks.filter(t => {
    if (hideDone && t.status === 'done') return false;
    // Personal tasks: only visible to creator, hidden from managers and others
    if (t.personal) {
      if (t.createdBy !== profile?.email) return false;
    }
    if (!t.personal && filterArtist !== 'all' && t.artistId !== filterArtist) return false;
    return true;
  });

  const byStatusPriority = (status, priority) =>
    visibleTasks.filter(t => t.status === status && t.priority === priority && !t.personal);

  const byPriority = (priority) =>
    visibleTasks.filter(t => t.priority === priority && !t.personal);

  const byStatusPersonal = (status) =>
    visibleTasks.filter(t => t.status === status && t.personal && t.createdBy === profile?.email);

  const personalTasks = visibleTasks.filter(t => t.personal && t.createdBy === profile?.email);

  // Cap = non-done, non-personal tasks with that priority, excluding dragged task
  const capCount = (priority, excludeId = null) =>
    tasks.filter(t => t.priority === priority && t.status !== 'done' && t.id !== excludeId && !t.personal).length;

  const artistName = (id) => artists.find(a => a.id === id)?.name || '';
  const artistColor = (id) => artists.find(a => a.id === id)?.color || 'var(--purple)';

  const handleAdd = async () => {
    if (!form.title.trim()) return;
    const taskData = form.personal
      ? { ...form, artistId: '', priority: 'personal', createdBy: profile?.email }
      : { ...form, artistId: form.artistId || (isManager ? '' : profile?.artistId), createdBy: profile?.email };
    await addTask(taskData);
    setForm({ title:'', artistId:'', priority:'P1', category:'music', status:'todo', dueDate:'', notes:'', personal:false });
    setShowAdd(false);
  };

  const handleDrop = (targetStatus, targetPriority) => {
    if (!dragTask) return;
    if (dragTask.status === targetStatus && dragTask.priority === targetPriority) { setDragTask(null); setDragOver(null); return; }
    // Cap check — only for todo/inprogress destinations
    if (targetStatus !== 'done' && targetPriority !== 'P2') {
      const group = PRIORITY_GROUPS.find(g => g.id === targetPriority);
      const count = capCount(targetPriority, dragTask.id);
      if (group.max && count >= group.max) {
        const existing = tasks.filter(t => t.priority === targetPriority && t.status !== 'done' && t.id !== dragTask.id);
        setDemoteModal({ dragTask, targetStatus, targetPriority, existing });
        setDragTask(null); setDragOver(null);
        return;
      }
    }
    updateTask(dragTask.id, { status: targetStatus, priority: targetPriority });
    setDragTask(null); setDragOver(null);
  };

  const handleDemote = async (taskToDemote) => {
    const demoteTo = demoteModal.targetPriority === 'P0' ? 'P1' : 'P2';
    await updateTask(taskToDemote.id, { priority: demoteTo });
    await updateTask(demoteModal.dragTask.id, { status: demoteModal.targetStatus, priority: demoteModal.targetPriority });
    setDemoteModal(null);
  };

  if (loading) return <div style={{ padding:32, color:'var(--muted)' }}>Loading tasks...</div>;

  const openCount = tasks.filter(t => t.status !== 'done').length;
  const doneCount = tasks.filter(t => t.status === 'done').length;

  return (
    <div style={{ padding:'28px 30px', position:'relative', zIndex:1 }}>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
        <div>
          <h1 style={{ fontSize:22, fontWeight:800, marginBottom:2 }}>✦ Task Board</h1>
          <div style={{ fontSize:12, color:'var(--muted)', fontFamily:'var(--mono)' }}>
            {openCount} open · {doneCount} done
          </div>
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          {/* View toggle */}
          <div style={{ display:'flex', gap:2, background:'var(--surface2)', padding:3, borderRadius:8, border:'1px solid var(--border)' }}>
            <button
              onClick={() => setViewMode('list')}
              title="List view"
              style={{ padding:'6px 10px', borderRadius:6, border:'none', cursor:'pointer', background:viewMode==='list'?'var(--surface3)':'transparent', color:viewMode==='list'?'var(--text)':'var(--muted)', transition:'all .15s' }}
            >
              <LayoutList size={14}/>
            </button>
            <button
              onClick={() => setViewMode('columns')}
              title="Column view"
              style={{ padding:'6px 10px', borderRadius:6, border:'none', cursor:'pointer', background:viewMode==='columns'?'var(--surface3)':'transparent', color:viewMode==='columns'?'var(--text)':'var(--muted)', transition:'all .15s' }}
            >
              <Columns size={14}/>
            </button>
          </div>
          {/* Hide done toggle */}
          <button
            onClick={() => setHideDone(!hideDone)}
            className={`btn btn-sm ${hideDone ? 'btn-primary' : 'btn-ghost'}`}
            title="Hide done tasks"
            style={{ display:'flex', alignItems:'center', gap:6 }}
          >
            <EyeOff size={13}/> {hideDone ? 'Showing active' : 'Hide done'}
          </button>
          <button onClick={() => setShowAdd(true)} className="btn btn-primary"><Plus size={15}/> New Task</button>
        </div>
      </div>


      {/* LIST VIEW - Priority groups */}
      {viewMode === 'list' && (
        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
          {PRIORITY_GROUPS.map(group => {
            const groupTasks = byPriority(group.id);
            if (groupTasks.length === 0) return null;
            const isDragTarget = dragOver?.priority === group.id && dragOver?.status === 'todo';
            return (
              <div
                key={group.id}
                style={{ background:'var(--surface)', border:'2px solid '+(isDragTarget ? group.color : group.color+'33'), borderRadius:12, overflow:'hidden', transition:'border-color .2s' }}
                onDragOver={e => { e.preventDefault(); setDragOver({ status: 'todo', priority: group.id }); }}
                onDragLeave={() => setDragOver(null)}
                onDrop={() => handleDrop('todo', group.id)}
              >
                <div style={{ padding:'12px 18px', background:group.color+'12', borderBottom:'1px solid '+group.color+'33', display:'flex', alignItems:'center', gap:10 }}>
                  <div style={{ width:10, height:10, borderRadius:'50%', background:group.color, flexShrink:0 }}/>
                  <div style={{ fontSize:13, fontWeight:700, color:group.color, flex:1 }}>
                    {group.id === 'P0' ? 'P0 — Critical' : group.id === 'P1' ? 'P1 — Important' : 'P2 — Nice to Have'}
                  </div>
                  {isDragTarget && <div style={{ fontSize:10, color:group.color, fontFamily:'var(--mono)' }}>Drop here</div>}
                </div>
                <div style={{ padding:'8px 10px', display:'flex', flexDirection:'column', gap:6 }}>
                  {groupTasks.map(task => (
                    <TaskRow
                      key={task.id} task={task}
                      artistName={artistName(task.artistId)}
                      artistColor={artistColor(task.artistId)}
                      priorityColor={group.color}
                      onEdit={() => setEditTask(task)}
                      onStatusChange={(s) => updateTask(task.id, { status: s })}
                      onDragStart={() => setDragTask(task)}
                      isManager={isManager} profile={profile}
                      done={task.status === 'done'}
                      showStatus
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Personal tasks section — list view */}
      {viewMode === 'list' && personalTasks.length > 0 && (
        <div
          style={{ background:'var(--surface)', border:'2px solid '+(dragOver?.priority==='personal'?'#4a9eed':'#4a9eed33'), borderRadius:12, overflow:'hidden', transition:'border-color .2s', marginTop:4 }}
          onDragOver={e => { e.preventDefault(); setDragOver({ status:'todo', priority:'personal' }); }}
          onDragLeave={() => setDragOver(null)}
          onDrop={() => handleDrop('todo', 'personal')}
        >
          <div style={{ padding:'12px 18px', background:'#4a9eed12', borderBottom:'1px solid #4a9eed33', display:'flex', alignItems:'center', gap:10 }}>
            <div style={{ width:10, height:10, borderRadius:'50%', background:'#4a9eed', flexShrink:0 }}/>
            <div style={{ fontSize:13, fontWeight:700, color:'#4a9eed', flex:1 }}>🔒 Personal</div>
            <div style={{ fontSize:10, color:'var(--dim)', fontFamily:'var(--mono)' }}>Only visible to you</div>
          </div>
          <div style={{ padding:'8px 10px', display:'flex', flexDirection:'column', gap:6 }}>
            {personalTasks.map(task => (
              <TaskRow
                key={task.id} task={task}
                artistName="" artistColor="#4a9eed"
                priorityColor="#4a9eed"
                onEdit={() => setEditTask(task)}
                onStatusChange={(s) => updateTask(task.id, { status: s })}
                onDragStart={() => setDragTask(task)}
                isManager={isManager} profile={profile}
                done={task.status === 'done'}
                showStatus personal
              />
            ))}
          </div>
        </div>
      )}

      {/* ── COLUMN VIEW ── */}
      {viewMode === 'columns' && (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:16 }}>
          {STATUS_COLS.filter(col => !(hideDone && col.id === 'done')).map(col => (
            <div key={col.id} style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:12, overflow:'hidden' }}>
              <div style={{ padding:'14px 18px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', gap:8 }}>
                <div style={{ width:8, height:8, borderRadius:'50%', background:col.color }}/>
                <div style={{ fontSize:13, fontWeight:700, flex:1 }}>{col.label}</div>
                <div style={{ fontSize:10, color:'var(--muted)', fontFamily:'var(--mono)' }}>
                  {visibleTasks.filter(t => t.status === col.id).length}
                </div>
              </div>
              <div style={{ padding:'10px', display:'flex', flexDirection:'column', gap:10, minHeight:200 }}>
                {PRIORITY_GROUPS.map(group => {
                  const groupTasks = byStatusPriority(col.id, group.id);
                  const isDragTarget = dragOver?.status === col.id && dragOver?.priority === group.id;

                  return (
                    <div
                      key={group.id}
                      style={{ borderRadius:8, border:'1px solid '+(isDragTarget ? group.color : group.color+'33'), background:isDragTarget ? group.color+'08' : 'transparent', transition:'all .18s', minHeight:44, padding:'6px 8px' }}
                      onDragOver={e => { e.preventDefault(); setDragOver({ status: col.id, priority: group.id }); }}
                      onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(null); }}
                      onDrop={() => handleDrop(col.id, group.id)}
                    >
                      <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom: groupTasks.length > 0 ? 6 : 2 }}>
                        <span style={{ fontSize:9, fontWeight:700, color:group.color, fontFamily:'var(--mono)', letterSpacing:.5 }}>{group.id}</span>

                        {isDragTarget && <span style={{ fontSize:9, color:group.color, fontFamily:'var(--mono)', marginLeft:'auto' }}>Drop</span>}
                      </div>
                      {groupTasks.map(task => (
                        <TaskRow
                          key={task.id} task={task}
                          artistName={artistName(task.artistId)}
                          artistColor={artistColor(task.artistId)}
                          priorityColor={group.color}
                          onEdit={() => setEditTask(task)}
                          onStatusChange={(s) => updateTask(task.id, { status: s })}
                          onDragStart={() => setDragTask(task)}
                          isManager={isManager} profile={profile}
                          done={col.id === 'done'}
                        />
                      ))}
                      {groupTasks.length === 0 && !isDragTarget && (
                        <div style={{ fontSize:9, color:'var(--dim)', fontFamily:'var(--mono)', paddingLeft:2 }}>—</div>
                      )}
                    </div>
                  );
                })}
                {/* Personal tasks in this column */}
                {byStatusPersonal(col.id).length > 0 && (
                  <div
                    style={{ borderRadius:8, border:'1px solid #4a9eed33', background:'transparent', transition:'all .18s', minHeight:44, padding:'6px 8px' }}
                    onDragOver={e => { e.preventDefault(); setDragOver({ status: col.id, priority:'personal' }); }}
                    onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(null); }}
                    onDrop={() => handleDrop(col.id, 'personal')}
                  >
                    <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:6 }}>
                      <span style={{ fontSize:9, fontWeight:700, color:'#4a9eed', fontFamily:'var(--mono)', letterSpacing:.5 }}>🔒 Personal</span>
                    </div>
                    {byStatusPersonal(col.id).map(task => (
                      <TaskRow
                        key={task.id} task={task}
                        artistName="" artistColor="#4a9eed"
                        priorityColor="#4a9eed"
                        onEdit={() => setEditTask(task)}
                        onStatusChange={(s) => updateTask(task.id, { status: s })}
                        onDragStart={() => setDragTask(task)}
                        isManager={isManager} profile={profile}
                        done={col.id === 'done'} personal
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Task Modal */}
      {showAdd && (
        <Modal onClose={() => setShowAdd(false)} title="New Task">
          <TaskForm form={form} setForm={setForm} artists={artists} isManager={isManager} profile={profile}/>
          <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
            <button onClick={() => setShowAdd(false)} className="btn btn-ghost">Cancel</button>
            <button onClick={handleAdd} className="btn btn-primary" disabled={!form.title.trim()}>Add Task</button>
          </div>
        </Modal>
      )}

      {/* Edit Task Modal */}
      {editTask && (
        <Modal onClose={() => setEditTask(null)} title="Edit Task">
          <TaskForm
            form={editTask}
            setForm={(updates) => setEditTask(t => typeof updates === 'function' ? updates(t) : {...t, ...updates})}
            artists={artists} isManager={isManager} profile={profile}
          />
          <div style={{ display:'flex', gap:10, justifyContent:'space-between' }}>
            {isManager && (
              <button onClick={() => { deleteTask(editTask.id); setEditTask(null); }} className="btn btn-sm" style={{ background:'rgba(192,57,43,.15)', color:'#e74c3c', border:'1px solid rgba(192,57,43,.3)' }}>
                Delete
              </button>
            )}
            <div style={{ display:'flex', gap:10, marginLeft:'auto' }}>
              <button onClick={() => setEditTask(null)} className="btn btn-ghost">Cancel</button>
              <button onClick={() => { updateTask(editTask.id, editTask); setEditTask(null); }} className="btn btn-primary">Save Changes</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Demote Modal */}
      {demoteModal && (
        <Modal onClose={() => setDemoteModal(null)} title={'⚠ '+demoteModal.targetPriority+' is at capacity'}>
          <div style={{ padding:'12px 16px', borderRadius:8, marginBottom:16, background:'rgba(192,57,43,.1)', border:'1px solid rgba(192,57,43,.3)' }}>
            <div style={{ fontSize:13, lineHeight:1.7 }}>
              You already have the max {PRIORITY_GROUPS.find(g=>g.id===demoteModal.targetPriority)?.max} active {demoteModal.targetPriority} tasks.
              To add <strong>"{demoteModal.dragTask.title}"</strong> as {demoteModal.targetPriority}, choose one to demote:
            </div>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:16 }}>
            {demoteModal.existing.map(task => (
              <button
                key={task.id} onClick={() => handleDemote(task)}
                style={{ padding:'12px 16px', borderRadius:8, background:'var(--surface2)', border:'1px solid var(--border)', cursor:'pointer', textAlign:'left', fontFamily:'var(--font)', transition:'border-color .15s' }}
                onMouseEnter={e => e.currentTarget.style.borderColor='var(--border-active)'}
                onMouseLeave={e => e.currentTarget.style.borderColor='var(--border)'}
              >
                <div style={{ fontSize:13, fontWeight:600, marginBottom:3 }}>{task.title}</div>
                <div style={{ fontSize:11, color:'var(--muted)' }}>
                  Demote to {demoteModal.targetPriority === 'P0' ? 'P1' : 'P2'} → add "{demoteModal.dragTask.title}" as {demoteModal.targetPriority}
                </div>
              </button>
            ))}
          </div>
          <div style={{ display:'flex', justifyContent:'flex-end' }}>
            <button onClick={() => setDemoteModal(null)} className="btn btn-ghost">Cancel</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function TaskRow({ task, artistName, artistColor, priorityColor, onEdit, onStatusChange, onDragStart, isManager, profile, done, showStatus }) {
  const canEdit = isManager || profile?.artistId === task.artistId;
  const isOverdue = task.dueDate && new Date(task.dueDate) < new Date() && task.status !== 'done';

  return (
    <div
      draggable={canEdit}
      onDragStart={onDragStart}
      onClick={canEdit ? onEdit : undefined}
      style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 10px', borderRadius:7, background:'var(--surface2)', border:'1px solid var(--border)', cursor:canEdit?'pointer':'default', transition:'all .15s', marginBottom:4, opacity:done?0.55:1 }}
      onMouseEnter={e => { if(canEdit) e.currentTarget.style.borderColor='var(--border-active)'; }}
      onMouseLeave={e => e.currentTarget.style.borderColor='var(--border)'}
    >
      {canEdit && (
        <div style={{ color:'var(--dim)', flexShrink:0, cursor:'grab' }}>
          <GripVertical size={12}/>
        </div>
      )}

      {/* Status toggle */}
      <button
        onClick={e => { e.stopPropagation(); onStatusChange(task.status==='done'?'todo':task.status==='todo'?'inprogress':'done'); }}
        style={{ width:16, height:16, borderRadius:3, flexShrink:0, border:'1.5px solid '+(done?'var(--teal2)':task.status==='inprogress'?'var(--purple2)':'var(--dim)'), background:done?'var(--teal2)':'transparent', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', transition:'all .15s' }}
      >
        {done && <Check size={10} style={{ color:'#fff' }}/>}
        {task.status==='inprogress'&&!done&&<div style={{ width:7, height:7, borderRadius:'50%', background:'var(--purple2)' }}/>}
      </button>

      {/* Title + meta */}
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontSize:11, fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', textDecoration:done?'line-through':'none' }}>
          {task.title}
        </div>
        <div style={{ display:'flex', gap:5, marginTop:2, flexWrap:'wrap', alignItems:'center' }}>
          {artistName && <span style={{ fontSize:9, padding:'1px 5px', borderRadius:20, fontWeight:600, background:artistColor+'22', color:artistColor, fontFamily:'var(--mono)' }}>{artistName}</span>}
          {showStatus && <span style={{ fontSize:9, padding:'1px 5px', borderRadius:20, background:STATUS_META[task.status]?.color+'22', color:STATUS_META[task.status]?.color, fontFamily:'var(--mono)' }}>{STATUS_META[task.status]?.label}</span>}
          {task.category && <span style={{ fontSize:9, color:'var(--dim)', fontFamily:'var(--mono)' }}>{task.category}</span>}
          {task.dueDate && <span style={{ fontSize:9, fontFamily:'var(--mono)', color:isOverdue?'#e74c3c':'var(--muted)', display:'flex', alignItems:'center', gap:2 }}>{isOverdue&&<AlertTriangle size={8}/>}{task.dueDate}</span>}
        </div>
      </div>

      <Edit2 size={10} style={{ color:'var(--dim)', flexShrink:0 }}/>
    </div>
  );
}

function TaskForm({ form, setForm, artists, isManager, profile }) {
  return (
    <>
      <div className="form-group"><label>Title</label><input value={form.title||""} onChange={e => setForm(f => ({...f, title:e.target.value}))} placeholder="Task title..." autoFocus/></div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
        {!form.personal && <div className="form-group"><label>Priority</label><select value={form.priority||"P1"} onChange={e => setForm(f => ({...f, priority:e.target.value}))}><option value="P0">P0 - Critical</option><option value="P1">P1 - Important</option><option value="P2">P2 - Nice to Have</option></select></div>}
        <div className="form-group"><label>Status</label><select value={form.status||"todo"} onChange={e => setForm(f => ({...f, status:e.target.value}))}><option value="todo">To Do</option><option value="inprogress">In Progress</option><option value="done">Done</option></select></div>
        {isManager && !form.personal && <div className="form-group"><label>Artist</label><select value={form.artistId||""} onChange={e => setForm(f => ({...f, artistId:e.target.value}))}><option value="">General</option>{artists.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}</select></div>}
        {!form.personal && <div className="form-group"><label>Category</label><select value={form.category||"music"} onChange={e => setForm(f => ({...f, category:e.target.value}))}>{["music","social","admin","collab","discussion"].map(c => <option key={c}>{c}</option>)}</select></div>}
        <div className="form-group"><label>Due Date</label><input type="date" value={form.dueDate||""} onChange={e => setForm(f => ({...f, dueDate:e.target.value}))}/></div>
      </div>
      <div className="form-group"><label>Notes</label><textarea rows={3} value={form.notes||""} onChange={e => setForm(f => ({...f, notes:e.target.value}))} placeholder="Optional notes..."/></div>
      <div className="form-group"><label style={{ display:"flex", alignItems:"center", gap:8, cursor:"pointer", fontSize:13, color:"var(--muted)", fontWeight:500 }}><input type="checkbox" checked={!!form.personal} onChange={e => setForm(f => ({...f, personal:e.target.checked, artistId: e.target.checked ? "" : f.artistId}))} style={{ width:"auto", accentColor:"#4a9eed" }}/>Personal - only visible to me</label></div>
    </>
  );
}
